/**
 * Bakery contact + large-order helpers.
 *
 * Big orders (e.g. 100 saucijzenbroodjes) can't always be honoured from the
 * day's stock, so there is a hard "max online" ceiling the webshop won't
 * exceed — for more the customer phones the bakery. The ceiling defaults
 * globally (StoreSettings) and can be overridden per product.
 */

/* ─── Contact constants (fallbacks for the bulk notice) ──────────────────── */
export const BAKERY_PHONE_HREF    = '+31548512752'
export const BAKERY_PHONE_DISPLAY = '0548 – 51 27 52'
export const BAKERY_HOURS_TEXT    = 'Ma–Za tijdens openingstijden'

/* ─── Global defaults (mirror StoreSettings defaults) ────────────────────── */
export const DEFAULT_BULK_MAX_ONLINE_QTY = 100

/* ─── Custom cakes (configurator pages, not Product rows) ────────────────── */
// Slagroomtaart met tekst needs extra preparation time: at least this many
// OPEN days between ordering and pickup/delivery. Shared by the page and the
// orders API so the promise and the enforcement can't drift apart.
export const TEKSTTAART_PRODUCT_ID    = 'TEKSTTAART'
export const TEKSTTAART_MIN_LEAD_DAYS = 2
export const FOTOTAART_PRODUCT_ID     = 'FOTOTAART'
// De fototaart heeft dezelfde voorbereidingstijd als de teksttaart: de print
// moet gemaakt worden en op de taart. Stond hier eerder niet, waardoor een
// fototaart voor de eerstvolgende dag besteld kon worden.
export const FOTOTAART_MIN_LEAD_DAYS  = 2

/** Voorbereidingstijd (open dagen) van de taarten die wij op maat maken. Zij
 *  hebben geen Product-rij, dus overal waar de voorbereidingstijd normaal van
 *  `Product.minLeadDays` komt, komt hij voor deze twee hiervandaan. */
export const CUSTOM_CAKE_LEAD_DAYS: Record<string, number> = {
  [TEKSTTAART_PRODUCT_ID]: TEKSTTAART_MIN_LEAD_DAYS,
  [FOTOTAART_PRODUCT_ID]:  FOTOTAART_MIN_LEAD_DAYS,
}

/** Gratis kaarsjes bij een taart. Meer dan dit aantal loopt via de winkel:
 *  de bakker wil dan even overleggen (bijzondere aantallen, speciale wensen). */
export const MAX_FREE_CANDLES = 10

/* ─── Wat de taarten op maat kosten ──────────────────────────────────────────
   De fototaart en de slagroomtaart met tekst hebben geen Product-rij, dus hun
   prijs kan niet uit de database komen zoals bij elk ander product. Hij stond
   daarom in de configuratorpagina's zelf — met als gevolg dat de browser de
   prijs bepaalde en een taart van € 49,50 voor één cent besteld kon worden.

   De tabellen staan nu hier: de pagina's tonen ze, en de order-API rekent de
   prijs ermee opnieuw uit (zie `priceCustomCake`). Eén bron, dus de winkel en
   de kassa kunnen niet uit elkaar lopen. */

export interface CakeSizeRow {
  id:          string
  label:       string
  description: string
  price:       number
}

/** Fotologotaart: foto of logo als eetbare print op een slagroomtaart.
 *  Maten en prijzen zoals de bakkerij ze opgaf (oktober 2026). */
export const FOTOTAART_SIZES: readonly CakeSizeRow[] = [
  { id: '12pers', label: '12 personen', description: 'rechthoekig', price: 39.95 },
  { id: '18pers', label: '18 personen', description: 'rechthoekig', price: 55.95 },
  { id: '20pers', label: '20 personen', description: 'rechthoekig', price: 62.50 },
  { id: '28pers', label: '28 personen', description: 'rechthoekig', price: 85.00 },
  { id: '32pers', label: '32 personen', description: 'rechthoekig', price: 95.50 },
  { id: '36pers', label: '36 personen', description: 'rechthoekig', price: 106.50 },
]

/** Slagroomtaart met tekst: de tekst zit bij de prijs in. */
export const TEKSTTAART_SIZES: readonly CakeSizeRow[] = [
  { id: '12pers', label: '12 personen', description: 'rechthoekig', price: 35.95 },
  { id: '18pers', label: '18 personen', description: 'rechthoekig', price: 52.45 },
  { id: '24pers', label: '24 personen', description: 'rechthoekig', price: 68.95 },
]

/** Toeslag voor de tekst op de slagroomtaart. Sinds oktober 2026 zit de tekst
 *  bij de prijs in (de prijslijst van de bakkerij noemt één prijs per maat),
 *  dus 0. De regel blijft bestaan zodat de kassa niets hoeft te weten van
 *  wanneer dat veranderde. */
export const CAKE_TEXT_PRICE = 0

/** De maattabel die bij een taart hoort, of null als het geen taart op maat is. */
export function cakeSizesFor(productId: string): readonly CakeSizeRow[] | null {
  if (productId === FOTOTAART_PRODUCT_ID)  return FOTOTAART_SIZES
  if (productId === TEKSTTAART_PRODUCT_ID) return TEKSTTAART_SIZES
  return null
}

/** De namen van de optiegroepen die de configurators meesturen. De order-API
 *  herkent de maat en de tekst hieraan; de rest (kaarsjes, opmerking, bewerkte
 *  foto) is gratis en hoeft alleen op de bon te komen. */
export const CAKE_SIZE_GROUP = 'Maat'
export const CAKE_TEXT_GROUP = 'Tekst op taart'

export interface CustomCakePrice {
  /** De uitgerekende prijs, of null als de maat niet klopt. */
  price: number | null
  /** Wat er misging, klaar om aan de klant te tonen. */
  error: string | null
}

/**
 * Rekent de prijs van een taart op maat uit op basis van wat de klant koos.
 * Server-authoritatief: dit is wat de order-API gebruikt, ongeacht welk bedrag
 * de browser meestuurt.
 *
 * De maat komt binnen als customization-regel `Maat` met het label uit de
 * tabel hierboven ("12 personen"). Een onbekende of ontbrekende maat is een
 * fout, geen reden om op € 0 uit te komen.
 */
export function priceCustomCake(
  productId: string,
  customizations: { groupName: string; optionName: string }[] = [],
): CustomCakePrice {
  const sizes = cakeSizesFor(productId)
  if (!sizes) return { price: null, error: 'Onbekend product' }

  const chosen = customizations.find((c) => c.groupName === CAKE_SIZE_GROUP)
  if (!chosen) {
    return { price: null, error: 'Kies eerst een maat voor de taart.' }
  }
  const size = sizes.find((s) => s.label === chosen.optionName || s.id === chosen.optionName)
  if (!size) {
    return { price: null, error: `"${chosen.optionName}" is geen maat die wij maken. Kies opnieuw een maat.` }
  }

  // De tekstregel op de slagroomtaart is de enige betaalde extra. De fototaart
  // heeft er geen, dus daar telt hij ook niet mee als de browser hem meestuurt.
  const wantsText =
    productId === TEKSTTAART_PRODUCT_ID &&
    customizations.some((c) => c.groupName === CAKE_TEXT_GROUP)

  return { price: size.price + (wantsText ? CAKE_TEXT_PRICE : 0), error: null }
}

/* ─── Waardebon (cadeaubon) ──────────────────────────────────────────────── */
// De klant kiest zelf het bedrag (vrij invulbaar, min € 10). Tot € 15 gaat de
// bon in een verzorgde envelop; vanaf € 15 pakken we hem standaard gratis
// feestelijk in (de klant kan dat desgewenst uitzetten). Online bestellen + afhalen in
// de winkel; besteden kan ALLEEN fysiek in de bakkerij. Het bedrag komt van
// de client en wordt server-side tegen deze grenzen gevalideerd.
export const WAARDEBON_PRODUCT_ID = 'WAARDEBON'
export const WAARDEBON_SLUG       = 'waardebon'
export const WAARDEBON_MIN_AMOUNT = 10
export const WAARDEBON_MAX_AMOUNT = 250
export const WAARDEBON_WRAP_FROM  = 15

/* ─── Laten snijden ──────────────────────────────────────────────────────── */
/* Taarten, vlaaien, cake en slofjes snijdt de bakkerij desgewenst in punten.
   Het is geen echte productoptie in de database maar een vlag op het product
   (`canBeSliced`), zodat de bakkerij het met één vinkje — of in bulk voor een
   hele categorie — kan aanzetten zonder per product een optiegroep te maken.
   De keuze reist mee als customization, zodat hij op de bon, in de mail en op
   de bestelling in Beheer verschijnt. Wat het snijden kost staat in Beheer
   (StoreSettings.slicingPrice, 0 = gratis) en wordt server-side herrekend. */
export const SLICING_GROUP_ID    = 'laten-snijden'
export const SLICING_GROUP_NAME  = 'Laten snijden'
export const SLICING_OPTION_ID   = 'ja'
export const SLICING_OPTION_NAME = 'In punten gesneden'

/** Wat het snijden kost als Beheer nog niets heeft ingevuld. Sluit aan bij
 *  het losse artikel "Taart snijden" (€ 1,35) uit de oude webshop. 0 = gratis. */
export const DEFAULT_SLICING_PRICE = 1.35

/** De customization-regel die "laten snijden" in de winkelwagen voorstelt.
 *  De prijs komt uit Beheer → de order-API herrekent hem server-side. */
export function slicingCustomization(price: number) {
  return {
    groupId:    SLICING_GROUP_ID,
    groupName:  SLICING_GROUP_NAME,
    optionId:   SLICING_OPTION_ID,
    optionName: SLICING_OPTION_NAME,
    priceAddon: price,
  }
}

/** Herkent de snij-optie, ook als hij alleen met namen is opgeslagen
 *  (bestelregels bewaren groupName/optionName, geen ids). */
export function isSlicingCustomization(
  c: { groupId?: string; groupName?: string; optionName?: string },
): boolean {
  if (c.groupId === SLICING_GROUP_ID) return true
  return c.groupName === SLICING_GROUP_NAME && c.optionName === SLICING_OPTION_NAME
}

/** Suffix op de cartKey, zodat dezelfde taart wél en niet gesneden naast
 *  elkaar in de wagen kunnen staan. */
export const SLICED_KEY_SUFFIX = ':gesneden'

export function withSlicedKey(cartKey: string, sliced: boolean): string {
  const base = cartKey.endsWith(SLICED_KEY_SUFFIX)
    ? cartKey.slice(0, -SLICED_KEY_SUFFIX.length)
    : cartKey
  return sliced ? base + SLICED_KEY_SUFFIX : base
}

/** Producten die je niet blind "opnieuw kunt bestellen": ze hangen aan een
 *  configuratie (foto, tekst op de taart, bonbedrag) die per bestelling
 *  opnieuw gemaakt moet worden. De re-order-knoppen slaan ze over en sturen
 *  de klant naar de bijbehorende pagina. */
export const CONFIGURABLE_PRODUCT_IDS: readonly string[] = [
  FOTOTAART_PRODUCT_ID,
  TEKSTTAART_PRODUCT_ID,
  WAARDEBON_PRODUCT_ID,
]

export function isReorderable(productId: string | null | undefined): boolean {
  return !!productId && !CONFIGURABLE_PRODUCT_IDS.includes(productId)
}

/**
 * True for products whose stored price is a floor the customer builds on
 * rather than what they will pay — today only the waardebon, where the amount
 * is free from € 10. Anywhere such a product shows a bare price it must read
 * "Vanaf € 10,00", and its add button must lead to the page carrying the
 * amount chooser instead of dropping the floor price straight into the cart.
 */
export function isFromPriceProduct(slug: string): boolean {
  return slug === WAARDEBON_SLUG
}

export interface BulkLimits {
  /** Hard ceiling the webshop won't exceed — for more the customer phones. */
  maxOnlineQty: number
}

interface BulkSettingsLike {
  bulkMaxOnlineQty?: number | null
}

interface ProductBulkOverrides {
  maxOnlineQty?: number | null
}

/**
 * Resolve a product's effective "max online" ceiling: per-product override
 * wins, else the store-wide default, else the hard-coded fallback.
 */
export function resolveBulkLimits(
  product: ProductBulkOverrides | null | undefined,
  settings: BulkSettingsLike | null | undefined,
): BulkLimits {
  const rawMax =
    product?.maxOnlineQty ??
    settings?.bulkMaxOnlineQty ??
    DEFAULT_BULK_MAX_ONLINE_QTY

  return { maxOnlineQty: Math.max(1, rawMax) }
}

/* ─── Seizoen (campaign) minimum-quantity rules ──────────────────────────── */

export interface SeasonRuleInput {
  campaignSlug?:    string | null
  seasonMinQty?:    number | null
  offSeasonMinQty?: number | null
}

export interface SeasonOrderRule {
  /** Can the product be put in the cart right now? */
  orderable: boolean
  /** Effective minimum quantity per order (1 = no minimum). */
  minQty: number
  /** True when we're outside the season but ordering is allowed via offSeasonMinQty. */
  outOfSeasonOrder: boolean
}

/**
 * Work out the ordering rule for a product that is (optionally) linked to a
 * seizoen. Inside the season: orderable with seasonMinQty (if any). Outside
 * the season: only orderable when offSeasonMinQty is set, from that quantity.
 * Products without a seizoen are always orderable with no minimum.
 */
export function resolveSeasonRule(
  product: SeasonRuleInput | null | undefined,
  seasonActive: boolean,
): SeasonOrderRule {
  if (!product?.campaignSlug) return { orderable: true, minQty: 1, outOfSeasonOrder: false }
  if (seasonActive) {
    return { orderable: true, minQty: Math.max(1, product.seasonMinQty ?? 1), outOfSeasonOrder: false }
  }
  if (product.offSeasonMinQty != null && product.offSeasonMinQty > 0) {
    return { orderable: true, minQty: Math.max(1, product.offSeasonMinQty), outOfSeasonOrder: true }
  }
  return { orderable: false, minQty: 1, outOfSeasonOrder: false }
}

/* ─── Bedrijfsgegevens voor op de factuur ────────────────────────────────── */
/* Aangeleverd door de bakkerij (oktober 2026). Het btw-identificatienummer
   ontbreekt nog; zolang het leeg is zet de factuur er "volgt" bij. Zie
   TE-BESPREKEN-MET-LISANNE.md. */
export const BAKERY_LEGAL = {
  name:            'Bakkerij Voortman',
  legalForm:       'Vennootschap onder firma',
  kvk:             '06083828',
  establishment:   '000008876673',
  street:          'Boomkamp 17',
  postalCode:      '7461 AW',
  city:            'Rijssen',
  email:           'info@bakkerijvoortmanrijssen.nl',
  /** Btw-id (NL…B01). Nog niet bekend. */
  vatId:           null as string | null,
} as const

/** Btw op brood en banket: het verlaagde tarief. */
export const VAT_RATE_FOOD = 9
